import { useState } from 'react';
import { FaChevronDown } from 'react-icons/fa';
import type { DailyForecast, MichiganTimezone } from '@miweather/shared';
import { WeatherIcon } from './WeatherIcon';

export function SevenDayForecast({ daily, timezone }: { daily: DailyForecast[]; timezone: MichiganTimezone }) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const handleToggle = (index: number) => {
    setExpandedIndex((prev) => (prev === index ? null : index));
  };

  return (
    <div className="md:w-72 md:shrink-0">
      <h2 className="text-base font-bold mb-3">7-Day Forecast</h2>
      <div className="space-y-2">
        {daily.map((day, i) => (
          <DailyRow
            key={i}
            day={day}
            isToday={i === 0}
            allDays={daily}
            expanded={expandedIndex === i}
            onToggle={() => handleToggle(i)}
            timezone={timezone}
          />
        ))}
      </div>
    </div>
  );
}

function DailyRow({
  day,
  isToday,
  allDays,
  expanded,
  onToggle,
  timezone,
}: {
  day: DailyForecast;
  isToday: boolean;
  allDays: DailyForecast[];
  expanded: boolean;
  onToggle: () => void;
  timezone: MichiganTimezone;
}) {
  const date = new Date(day.date);
  const dayName = isToday ? 'Today' : date.toLocaleDateString('en-US', { weekday: 'short', timeZone: timezone });
  const fullDayName = isToday ? 'Today' : date.toLocaleDateString('en-US', { weekday: 'long', timeZone: timezone });

  const allLows = allDays.map(d => d.low);
  const allHighs = allDays.map(d => d.high);
  const minRange = Math.min(...allLows) - 5;
  const maxRange = Math.max(...allHighs) + 5;

  const lowPct = Math.max(0, Math.min(100, ((day.low - minRange) / (maxRange - minRange)) * 100));
  const highPct = Math.max(0, Math.min(100, ((day.high - minRange) / (maxRange - minRange)) * 100));

  const hasDetailedForecast = day.detailedForecast || day.nightForecast;

  return (
    <div className="rounded-xl stat-box overflow-hidden">
      <button
        onClick={() => hasDetailedForecast && onToggle()}
        className={`w-full flex items-center gap-2 py-2.5 px-3 ${hasDetailedForecast ? 'cursor-pointer hover:bg-white/5' : ''} transition-colors`}
      >
        <div className="w-4 flex justify-center shrink-0">
          {hasDetailedForecast && (
            <FaChevronDown
              className={`text-xs text-white/40 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
            />
          )}
        </div>

        <div className="w-7 flex justify-center shrink-0">
          <WeatherIcon icon={day.icon} size={22} className="text-white/80" />
        </div>

        <div className="w-12 shrink-0 text-left">
          <span className="text-base font-medium">{dayName}</span>
        </div>

        <span className="w-9 text-right text-base font-bold text-[#f97316] shrink-0">
          {Math.round(day.low)}°
        </span>

        <div className="flex-1 h-[6px] bg-white/10 rounded-full relative mx-1">
          <div
            className="absolute top-0 bottom-0 rounded-full"
            style={{
              left: `${lowPct}%`,
              right: `${100 - highPct}%`,
              background: 'linear-gradient(to right, #4ba3c3, #f9c846, #f97316)',
            }}
          />
        </div>

        <span className="w-9 text-base font-bold text-[#f97316] shrink-0">
          {Math.round(day.high)}°
        </span>
      </button>

      {expanded && hasDetailedForecast && (
        <div className="px-4 pb-3 pt-2 border-t border-white/10 space-y-3">
          {day.detailedForecast && (
            <div>
              <p className="text-sm text-white/90 font-semibold mb-1">{fullDayName}</p>
              <p className="text-sm text-white/70">{day.detailedForecast}</p>
            </div>
          )}
          {day.nightForecast && (
            <div>
              <p className="text-sm text-white/90 font-semibold mb-1">{isToday ? 'Tonight' : `${fullDayName} Night`}</p>
              <p className="text-sm text-white/70">{day.nightForecast}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
